import React from 'react';
import { useTaskPriorityDistribution } from '../hooks';

export interface TaskPriorityDistributionProps {
  isLoading?: boolean;
  height?: number;
}

const TaskPriorityDistribution: React.FC<TaskPriorityDistributionProps> = ({ isLoading, height = 300 }) => {
  const { data: priorityData, isLoading: dataLoading } = useTaskPriorityDistribution();

  const priorityColors: Record<string, string> = {
    Critical: '#ef4444',
    High: '#f97316',
    Medium: '#f59e0b',
    Low: '#eab308',
    Normal: '#6b7280',
  };

  if (isLoading || dataLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Priority Distribution</h2>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!priorityData || priorityData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Priority Distribution</h2>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No task data available
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...priorityData.map(d => d.count), 0);
  const chartHeight = height - 100;
  const barWidth = Math.max(40, (600 / priorityData.length) - 15);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Priority Distribution</h2>
      <div className="space-y-4">
        <div className="flex items-end justify-center gap-4" style={{ height: `${chartHeight}px` }}>
          {priorityData.map((item, index) => {
            const heightPercent = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
            const barHeight = (chartHeight * heightPercent) / 100;
            const color = priorityColors[item.priority] || '#6b7280';
            
            return (
              <div
                key={index}
                className="flex flex-col items-center group relative"
                style={{ minWidth: `${barWidth}px` }}
              >
                <div className="w-full flex flex-col items-center justify-end" style={{ height: `${chartHeight}px` }}>
                  <div
                    className="w-full rounded-t transition-all hover:opacity-80 cursor-pointer relative"
                    style={{
                      height: `${barHeight}px`,
                      backgroundColor: color,
                      minHeight: barHeight > 0 ? '4px' : '0',
                    }}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      {item.count} tasks
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-600 text-center font-medium">{item.priority}</div>
                <div className="text-xs text-gray-400 mt-1">{item.count}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TaskPriorityDistribution;

